import { useCallback, useEffect, useMemo, useState } from "react";

import {
    Alert,
    Card,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    TextField,
    Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InboxIcon from "@mui/icons-material/Inbox";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
    COLORS,
    cardSx,
    FinanceCard,
    FinanceKpiTile,
    FinanceSectionHeader,
    SPACE,
} from "../shared/FinanceUiKit";
import { toLocalDateInputValue } from "../shared/dateInput";
import { getCost, getCostCurrency, getCurrency, getRevenue } from "../shared/jobFinancials";
import { getBilledAmount } from "../shared/vatDisplay";
import { fetchExchangeRates } from "../../../../OceanTMS/oceanDashboardUi";

import { financeText, getFinanceLanguage, saveFinanceLanguage } from "finance-language";
// Koyu hero bandı için (yedekten geri yüklendi)
import { Chip } from "@mui/material";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const toDateInputValue = (date) => toLocalDateInputValue(date);

const formatMoney = (amount, currency) => {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency || "TRY",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(amount) || 0);
    } catch {
        return `${Number(amount || 0).toFixed(2)} ${currency || ""}`;
    }
};

const formatAmount = (amount) =>
    new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
        Number(amount) || 0,
    );

// One row per currency (code left, amount right-aligned in tabular figures)
// instead of a single "TRY … · $… · €…" line that wrapped unpredictably
// inside the KPI tile once more than two currencies were present.
//
// Underneath, every currency converted to TRY at today's rate (the same
// exchangerate-api.com source the navbar's USD/EUR chips use), so the tile
// also answers "how much is this altogether". Hidden until rates load, or if
// any listed currency has no rate (a partial sum would read as the total).
const tryPerUnit = (rates, currency) => {
    if (currency === "TRY") return 1;
    const tryRate = Number(rates?.rates?.TRY);
    const currencyRate = Number(rates?.rates?.[currency]);
    return tryRate > 0 && currencyRate > 0 ? tryRate / currencyRate : null;
};

const CurrencyTotals = ({ lines, rates }) => {
    const perUnit = lines.map(([currency]) => tryPerUnit(rates, currency));
    const tryTotal = perUnit.every((rate) => rate !== null)
        ? lines.reduce((sum, [, amount], index) => sum + (Number(amount) || 0) * perUnit[index], 0)
        : null;
    const rateNote = lines
        .map(([currency], index) => (currency === "TRY" ? null : `1 ${currency} = ${formatAmount(perUnit[index])}`))
        .filter(Boolean)
        .join(" · ");

    return (
    <MDBox component="dl" m={0} display="flex" flexDirection="column">
        {lines.map(([currency, amount], index) => (
            <MDBox
                key={currency}
                display="flex"
                alignItems="baseline"
                justifyContent="space-between"
                gap={1.5}
                py={0.6}
                sx={{ borderTop: index ? `1px solid ${COLORS.border}` : "none" }}
            >
                <MDTypography
                    component="dt"
                    sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em", color: COLORS.subtext }}
                >
                    {currency}
                </MDTypography>
                <MDTypography
                    component="dd"
                    m={0}
                    sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: COLORS.text,
                        fontVariantNumeric: "tabular-nums",
                        lineHeight: 1.3,
                    }}
                >
                    {formatAmount(amount)}
                </MDTypography>
            </MDBox>
        ))}
        {tryTotal !== null && lines.length > 1 && (
            <MDBox mt={0.75} pt={0.9} sx={{ borderTop: `2px solid ${COLORS.text}` }}>
                <MDBox display="flex" alignItems="baseline" justifyContent="space-between" gap={1.5}>
                    <MDTypography
                        component="dt"
                        sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.04em", color: COLORS.text }}
                    >
                        {financeText("TOTAL TRY")}
                    </MDTypography>
                    <MDTypography
                        component="dd"
                        m={0}
                        sx={{
                            fontSize: 17,
                            fontWeight: 800,
                            color: COLORS.purple,
                            fontVariantNumeric: "tabular-nums",
                            lineHeight: 1.3,
                        }}
                    >
                        {formatAmount(tryTotal)}
                    </MDTypography>
                </MDBox>
                <MDTypography display="block" mt={0.25} sx={{ fontSize: 11, color: COLORS.subtext }}>
                    {financeText("At today's rate")}
                    {rateNote ? `: ${rateNote}` : ""}
                </MDTypography>
            </MDBox>
        )}
    </MDBox>
    );
};

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? value
        : new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(date);
};

// The shared FinanceUiKit amber (#B54708) is a dark brownish orange that
// reads too close to COLORS.red (#B42318) at a glance -- Pending and Rejected
// were hard to tell apart. This is a brighter, more saturated orange used
// only for Pending on this page, chosen specifically to stay unambiguous
// against blue/green/red.
const PENDING_COLOR = "#F79009";

// The EDM status column used to render every status in green regardless of
// its actual meaning, so a rejected invoice looked identical to an accepted
// one. Color by what the status text actually says instead. "Receive" is
// EDM's most common real-world status (the invoice reached the recipient,
// no accept/reject decision yet) -- it used to fall through to the same
// neutral gray as a genuinely unrecognized status, making it look unstyled,
// so it gets its own blue "in transit / awaiting decision" color.
const getStatusColor = (status) => {
    const normalized = String(status || "").toLowerCase();
    if (normalized.includes("accept") || normalized.includes("kabul")) return COLORS.green;
    if (normalized.includes("reject") || normalized.includes("ret")) return COLORS.red;
    if (normalized.includes("cancel") || normalized.includes("iptal")) return COLORS.red;
    if (normalized.includes("pending") || normalized.includes("bekle")) return PENDING_COLOR;
    if (normalized.includes("receive") || normalized.includes("alın")) return COLORS.blue;
    return COLORS.subtext;
};

// Turkish and English put the count in different places in this sentence
// ("Seçilen aralıkta 50 kayıt" vs "50 records in the selected range") --
// simply gluing a translated fragment after the number read as broken word
// order in Turkish ("50 seçilen aralıkta kayıt"), so this composes the whole
// sentence per language instead of concatenating a translated fragment.
const formatRecordCountLabel = (count, language) =>
    language === "tr"
        ? `Seçilen aralıkta ${count} kayıt`
        : `${count} record${count === 1 ? "" : "s"} in the selected range`;

// A rejected / cancelled incoming invoice is not a payable: linking it would
// turn it into supplier cost and open payable on a job, so it can't be linked
// and it is left out of the "awaiting link" and "total amount" figures.
const isRejectedStatus = (status) => {
    const normalized = String(status || "").toLowerCase();

    return (
        normalized.includes("reject") ||
        normalized.includes("cancel") ||
        normalized.includes("iptal")
    );
};

const STATUS_LEGEND = [
    { color: COLORS.blue, label: "Received", description: "Invoice reached the recipient, no decision yet" },
    { color: COLORS.green, label: "Accepted", description: "Recipient approved the invoice" },
    { color: COLORS.red, label: "Rejected", description: "Recipient rejected or cancelled the invoice" },
    { color: PENDING_COLOR, label: "Pending", description: "Awaiting a decision from the recipient" },
];

// A persistent, always-visible legend card (not just a hover tooltip) so the
// status color coding is discoverable without the user having to find and
// hover a small icon buried in a table header.
const StatusGuideCard = () => (
    <FinanceCard sx={{ mb: SPACE.sectionGap }}>
        <MDBox p={SPACE.cardPadding}>
            <FinanceSectionHeader
                title={financeText("Status guide")}
                subtitle={financeText("What each EDM status on this page means")}
                action={<InfoOutlinedIcon sx={{ fontSize: 22, color: COLORS.blue }} />}
                sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
                {STATUS_LEGEND.map((item) => (
                    <Grid item xs={12} sm={6} md={3} key={item.label}>
                        <MDBox
                            display="flex"
                            alignItems="flex-start"
                            gap={1.25}
                            p={1.5}
                            sx={{ borderRadius: SPACE.radiusTile, background: COLORS.muted, height: "100%" }}
                        >
                            <MDBox
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    background: item.color,
                                    mt: "5px",
                                    flexShrink: 0,
                                }}
                            />
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold" display="block" sx={{ color: COLORS.text, lineHeight: 1.3 }}>
                                    {financeText(item.label)}
                                </MDTypography>
                                <MDTypography variant="caption" display="block" sx={{ color: COLORS.subtext, lineHeight: 1.3 }}>
                                    {financeText(item.description)}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    </Grid>
                ))}
            </Grid>
        </MDBox>
    </FinanceCard>
);

// EDM's real limit was confirmed by live testing against its API: a 60-day
// span gets rejected ("tarih aralığı 2 ayı geçemez"), a 59-day span succeeds.
// So 59 days is the widest safe default, not the "2 calendar months" or flat
// 60-day guesses tried earlier. Computed fresh from the current date on every
// page load (not stored/cached), so it always reflects "today minus 59 days".
const defaultRange = () => {
    const end = new Date();
    const start = new Date(end.getTime() - 59 * 24 * 60 * 60 * 1000);
    return { startDate: toDateInputValue(start), endDate: toDateInputValue(end) };
};

const IncomingInvoices = () => {
    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [range, setRange] = useState(defaultRange);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [linkTarget, setLinkTarget] = useState(null);
    const [linkMode, setLinkMode] = useState("match");
    const [linkShipmentNo, setLinkShipmentNo] = useState("");
    const [linkDueDate, setLinkDueDate] = useState("");
    const [linking, setLinking] = useState(false);
    const [linkError, setLinkError] = useState("");
    const [openJobs, setOpenJobs] = useState([]);
    const [loadingOpenJobs, setLoadingOpenJobs] = useState(false);
    const [jobSearchQuery, setJobSearchQuery] = useState("");
    const [selectedJob, setSelectedJob] = useState(null);
    const [costWarning, setCostWarning] = useState(null);
    const [resolvingMismatch, setResolvingMismatch] = useState(false);
    const [mismatchError, setMismatchError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    // { invoice, url, loading, error } for the PDF preview dialog.
    const [viewer, setViewer] = useState(null);
    // USD-based rates (cached 12h, shared with the dashboards) for the
    // Total Amount tile's TRY grand total.
    const [exchangeRates, setExchangeRates] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchExchangeRates().then((data) => {
            if (!cancelled) setExchangeRates(data);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const openViewer = useCallback(async (invoice) => {
        if (!invoice?.uuid) return;

        setViewer({ invoice, url: "", loading: true, error: "" });

        try {
            const token = localStorage.getItem("token");
            const params = new URLSearchParams(invoice.createdAt ? { createdAt: invoice.createdAt } : {});
            const response = await fetch(
                `${API_URL}/edm/incoming-invoices/${encodeURIComponent(invoice.uuid)}/pdf?${params}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.error || financeText("Invoice could not be loaded."));
            }

            const url = URL.createObjectURL(await response.blob());
            setViewer((current) =>
                current?.invoice?.uuid === invoice.uuid ? { ...current, url, loading: false } : current,
            );
        } catch (requestError) {
            setViewer((current) =>
                current?.invoice?.uuid === invoice.uuid
                    ? {
                        ...current,
                        loading: false,
                        error: requestError.message || financeText("Invoice could not be loaded."),
                    }
                    : current,
            );
        }
    }, []);

    const closeViewer = () => setViewer(null);

    // Release the blob URL whenever the previewed PDF changes or closes.
    useEffect(() => {
        const url = viewer?.url;
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
    }, [viewer?.url]);

    useEffect(() => {
        const handler = (event) => setFinanceLanguageState(event.detail);
        window.addEventListener("finance-language-change", handler);
        return () => window.removeEventListener("finance-language-change", handler);
    }, []);

    const fetchIncoming = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            const params = new URLSearchParams({
                startDate: range.startDate,
                endDate: range.endDate,
            });

            const response = await fetch(`${API_URL}/edm/incoming-invoices?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const payload = await response.json().catch(() => ({}));

            if (!response.ok || !payload?.success) {
                throw new Error(payload?.error || financeText("Incoming invoices could not be loaded."));
            }

            setInvoices(Array.isArray(payload.invoices) ? payload.invoices : []);
        } catch (requestError) {
            setError(requestError.message || financeText("Incoming invoices could not be loaded."));
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    }, [range.startDate, range.endDate]);

    useEffect(() => {
        fetchIncoming();
    }, [fetchIncoming]);

    const fetchOpenJobs = useCallback(async (currency) => {
        setLoadingOpenJobs(true);

        try {
            const token = localStorage.getItem("token");
            const params = new URLSearchParams();
            if (currency) params.set("currency", currency);

            const response = await fetch(`${API_URL}/finance/supplier-costs/open?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const payload = await response.json().catch(() => ({}));

            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || financeText("Open jobs could not be loaded."));
            }

            // Cost and selling computed exactly like the Finance Jobs table
            // (COST / REVENUE columns) so the picker shows the same figures.
            const jobs = (Array.isArray(payload.data) ? payload.data : []).map((job) => {
                const financials = job.financials || {};
                return {
                    ...job,
                    expectedAmount: getCost(financials),
                    currency: getCostCurrency(financials),
                    revenueAmount: getBilledAmount(financials, getRevenue(financials)),
                    revenueCurrency: getCurrency(financials),
                };
            });

            setOpenJobs(jobs);
        } catch (requestError) {
            setOpenJobs([]);
        } finally {
            setLoadingOpenJobs(false);
        }
    }, []);

    const openLinkDialog = (invoice) => {
        if (isRejectedStatus(invoice?.status)) return;

        setLinkTarget(invoice);
        setLinkMode("match");
        setLinkShipmentNo("");
        setLinkDueDate("");
        setLinkError("");
        setJobSearchQuery("");
        setSelectedJob(null);
        setOpenJobs([]);
        fetchOpenJobs(invoice.currency);
    };

    const closeLinkDialog = () => {
        if (linking) return;
        setLinkTarget(null);
    };

    const filteredOpenJobs = useMemo(() => {
        const query = jobSearchQuery.trim().toLowerCase();
        if (!query) return openJobs;

        return openJobs.filter(
            (job) =>
                (job.shipmentNo || "").toLowerCase().includes(query) ||
                (job.customerName || "").toLowerCase().includes(query) ||
                (job.supplierName || "").toLowerCase().includes(query),
        );
    }, [openJobs, jobSearchQuery]);

    const submitLink = async () => {
        if (!linkTarget) return;

        const isMatchMode = linkMode === "match";

        if (isMatchMode && !selectedJob) {
            setLinkError(financeText("Select a job to link this invoice to."));
            return;
        }

        if (!isMatchMode && !linkShipmentNo.trim()) {
            setLinkError(financeText("Job / shipment number is required."));
            return;
        }

        setLinking(true);
        setLinkError("");

        const targetShipmentNo = isMatchMode ? selectedJob.shipmentNo : linkShipmentNo.trim();

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/finance/supplier-invoices`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    supplierName: linkTarget.supplierName,
                    invoiceNo: linkTarget.id,
                    invoiceDate: linkTarget.issueDate,
                    dueDate: linkDueDate || undefined,
                    totalAmount: linkTarget.amount,
                    netPayableAmount: linkTarget.amount,
                    currency: linkTarget.currency,
                    ...(isMatchMode
                        ? { financeJobId: selectedJob.financeJobId }
                        : {}),
                    shipmentNo: targetShipmentNo,
                    jobNo: targetShipmentNo,
                    edmSourceUuid: linkTarget.uuid,
                    // Lets the backend read this invoice's VAT-exclusive
                    // total from EDM for the cost comparison.
                    edmCreatedAt: linkTarget.createdAt,
                }),
            });

            const payload = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(payload?.message || financeText("This incoming invoice could not be linked."));
            }

            setInvoices((previous) =>
                previous.map((item) =>
                    item.uuid === linkTarget.uuid
                        ? {
                            ...item,
                            linked: true,
                            linkedSupplierInvoiceId: payload?.data?._id || payload?.data?.id || null,
                        }
                        : item,
                ),
            );
            setLinkTarget(null);
            setSelectedJob(null);

            if (payload?.costWarning) {
                setCostWarning({
                    ...payload.costWarning,
                    shipmentNo: targetShipmentNo,
                });
            } else if (payload?.costMatch?.status === "auto_approved") {
                setSuccessMessage(
                    financeText("Invoice matches the TMS cost within tolerance; job cost updated from supplier invoices for job") +
                        ` ${targetShipmentNo}.`,
                );
                setTimeout(() => setSuccessMessage(""), 6000);
            }
        } catch (requestError) {
            setLinkError(requestError.message || financeText("This incoming invoice could not be linked."));
        } finally {
            setLinking(false);
        }
    };

    // Only ever called from the user explicitly clicking "Approve and Update
    // Cost" on the mismatch warning — never automatic. Syncs the job's
    // expected cost to what's actually been invoiced.
    const resolveCostMismatch = async () => {
        if (!costWarning?.financeJobId) return;

        setResolvingMismatch(true);
        setMismatchError("");

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/finance/supplier-costs/resolve-mismatch`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ financeJobId: costWarning.financeJobId }),
            });

            const payload = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(payload?.message || financeText("Cost could not be updated."));
            }

            setSuccessMessage(
                financeText("Cost updated to match the invoiced amount for job") + ` ${costWarning.shipmentNo}.`,
            );
            setCostWarning(null);
            setTimeout(() => setSuccessMessage(""), 6000);
        } catch (requestError) {
            setMismatchError(requestError.message || financeText("Cost could not be updated."));
        } finally {
            setResolvingMismatch(false);
        }
    };

    const stats = useMemo(() => {
        const linkedCount = invoices.filter((item) => item.linked).length;
        const rejectedCount = invoices.filter((item) =>
            isRejectedStatus(item.status),
        ).length;
        const awaitingCount = invoices.filter(
            (item) => !item.linked && !isRejectedStatus(item.status),
        ).length;

        const amountByCurrency = new Map();
        invoices.forEach((item) => {
            if (isRejectedStatus(item.status)) return;

            const currency = item.currency || "TRY";
            amountByCurrency.set(currency, (amountByCurrency.get(currency) || 0) + (Number(item.amount) || 0));
        });

        const CURRENCY_ORDER = ["TRY", "USD", "EUR", "GBP"];
        const rank = (currency) => {
            const index = CURRENCY_ORDER.indexOf(currency);
            return index === -1 ? CURRENCY_ORDER.length : index;
        };
        const amountLines = amountByCurrency.size
            ? [...amountByCurrency.entries()].sort(
                ([a], [b]) => rank(a) - rank(b) || a.localeCompare(b),
            )
            : [["TRY", 0]];

        return {
            total: invoices.length,
            linked: linkedCount,
            rejected: rejectedCount,
            awaiting: awaitingCount,
            amountLines,
        };
    }, [invoices]);

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("Supplier"),
                    accessor: "supplierName",
                    width: "30%",
                    Cell: ({ value }) => {
                        const fullName = value || "—";
                        const truncated = fullName.length > 30 ? `${fullName.slice(0, 30)}…` : fullName;
                        return (
                            <Tooltip title={fullName.length > 30 ? fullName : ""} disableHoverListener={fullName.length <= 30}>
                                <MDTypography variant="button" fontWeight="medium" sx={{ color: COLORS.text }}>
                                    {truncated}
                                </MDTypography>
                            </Tooltip>
                        );
                    },
                },
                {
                    Header: financeText("Invoice No"),
                    accessor: "id",
                    Cell: ({ value }) => (
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {value || "—"}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("Issue Date"),
                    accessor: "issueDate",
                    Cell: ({ value }) => (
                        <MDTypography variant="caption" sx={{ color: COLORS.text }}>
                            {formatDate(value)}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("Amount"),
                    accessor: "amount",
                    Cell: ({ row }) => (
                        <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS.text }}>
                            {formatMoney(row.original.amount, row.original.currency)}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("Type"),
                    accessor: "sendType",
                    Cell: ({ value }) => (
                        <MDBox
                            component="span"
                            sx={{
                                display: "inline-flex",
                                px: 1.1,
                                py: 0.35,
                                borderRadius: SPACE.radiusPill,
                                fontSize: "12px",
                                fontWeight: 700,
                                color: value === "e-Fatura" ? COLORS.blue : COLORS.purple,
                                background: value === "e-Fatura" ? COLORS.blueLight : COLORS.purpleLight,
                            }}
                        >
                            {value || "—"}
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("Status"),
                    accessor: "status",
                    Cell: ({ row }) => {
                        const color = getStatusColor(row.original.status);
                        return (
                            <MDBox>
                                <MDTypography variant="caption" fontWeight="bold" display="block" sx={{ color }}>
                                    {row.original.status || "—"}
                                </MDTypography>
                                {row.original.statusDescription && (
                                    <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                        {row.original.statusDescription}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: "",
                    accessor: "actions",
                    // Clicks here act on the button/label, not the row-level
                    // "open invoice preview" handler.
                    Cell: ({ row }) => (
                        <MDBox onClick={(event) => event.stopPropagation()} sx={{ cursor: "default" }}>
                        {row.original.linked ? (
                            <MDBox display="flex" alignItems="center" gap={0.6}>
                                <CheckCircleIcon sx={{ fontSize: 18, color: COLORS.green }} />
                                <MDTypography variant="caption" fontWeight="bold" sx={{ color: COLORS.green }}>
                                    {financeText("Linked")}
                                </MDTypography>
                            </MDBox>
                        ) : isRejectedStatus(row.original.status) ? (
                            <Tooltip
                                title={financeText("Rejected invoices cannot be linked to a job.")}
                                arrow
                            >
                                <MDTypography variant="caption" fontWeight="bold" sx={{ color: COLORS.subtext }}>
                                    {financeText("Not linkable")}
                                </MDTypography>
                            </Tooltip>
                        ) : (
                            <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<LinkIcon sx={{ fontSize: "16px !important" }} />}
                                onClick={() => openLinkDialog(row.original)}
                            >
                                {financeText("Link to Job")}
                            </MDButton>
                        )}
                        </MDBox>
                    ),
                },
            ],
            rows: invoices,
        }),
        [invoices, financeLanguage],
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox py={3} sx={{ background: COLORS.page }}>
                <MDBox px={{ xs: 2, md: 3 }}>
                    <Card
                        sx={{
                            borderRadius: "22px",
                            color: COLORS.white,
                            background: "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",
                            boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",
                            overflow: "hidden",
                            position: "relative",
                            mb: SPACE.sectionGap,
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                width: 360,
                                height: 360,
                                borderRadius: "50%",
                                right: -130,
                                top: -180,
                                background: "rgba(255,255,255,.07)",
                            },
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                width: 230,
                                height: 230,
                                borderRadius: "50%",
                                right: 120,
                                bottom: -170,
                                background: "rgba(11,165,236,.16)",
                            },
                        }}
                    >
                        <MDBox p={{ xs: 2.5, md: 3.5 }} position="relative" zIndex={1}>
                            <Grid container spacing={3} alignItems="center">
                                <Grid item xs={12} lg={8}>
                                    <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                        <MDBox
                                            width={34}
                                            height={34}
                                            borderRadius="10px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ background: "rgba(255,255,255,.12)" }}
                                        >
                                            <CloudDownloadIcon sx={{ color: "#FFFFFF", fontSize: 20 }} />
                                        </MDBox>
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{ color: "rgba(255,255,255,.72)", letterSpacing: ".12em" }}
                                        >
                                            {financeText("EDM INCOMING INVOICE CENTER")}
                                        </MDTypography>
                                    </MDBox>

                                    <MDTypography variant="h2" fontWeight="bold" sx={{ color: "#FFFFFF", lineHeight: 1.12 }}>
                                        {financeText("Incoming Invoices")}
                                    </MDTypography>

                                    <MDTypography variant="body2" mt={1} sx={{ color: "rgba(255,255,255,.72)", maxWidth: 720 }}>
                                        {financeText("Invoices received through EDM (e-Fatura / e-Arşiv addressed to this company)")}
                                    </MDTypography>

                                    <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1} mt={2.5}>
                                        <Chip
                                            icon={<CheckCircleIcon sx={{ color: "#75E0A7 !important" }} />}
                                            label={formatRecordCountLabel(invoices.length, financeLanguage)}
                                            sx={{
                                                color: "#FFFFFF",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.14)",
                                            }}
                                        />
                                    </MDBox>
                                </Grid>

                                <Grid item xs={12} lg={4} display="flex" justifyContent={{ xs: "flex-start", lg: "flex-end" }}>
                                    <MDBox
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            p: "4px",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.20)",
                                            borderRadius: "12px",
                                            boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                        }}
                                    >
                                        <MDBox
                                            component="button"
                                            type="button"
                                            onClick={() => changeFinanceLanguage({ target: { value: "en" } })}
                                            title="English"
                                            sx={{
                                                appearance: "none",
                                                WebkitAppearance: "none",
                                                border: "none",
                                                outline: "none",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "7px",
                                                height: 38,
                                                minWidth: 76,
                                                px: 1.15,
                                                borderRadius: "8px",
                                                background: financeLanguage === "en" ? "#FFFFFF" : "transparent",
                                                color: financeLanguage === "en" ? COLORS.navy : "#FFFFFF",
                                                boxShadow: financeLanguage === "en" ? "0 3px 10px rgba(0,0,0,.18)" : "none",
                                                transition: "all .18s ease",
                                                "&:hover": {
                                                    background: financeLanguage === "en" ? "#FFFFFF" : "rgba(255,255,255,.12)",
                                                },
                                            }}
                                        >
                                            <MDBox
                                                component="img"
                                                src="/flags/gb.svg"
                                                alt="English"
                                                sx={{ width: 24, height: 17, objectFit: "cover", borderRadius: "3px", display: "block" }}
                                            />
                                            <MDTypography component="span" sx={{ fontSize: "12px", fontWeight: 800, color: "inherit", lineHeight: 1 }}>
                                                EN
                                            </MDTypography>
                                        </MDBox>

                                        <MDBox
                                            component="button"
                                            type="button"
                                            onClick={() => changeFinanceLanguage({ target: { value: "tr" } })}
                                            title="Türkçe"
                                            sx={{
                                                appearance: "none",
                                                WebkitAppearance: "none",
                                                border: "none",
                                                outline: "none",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "7px",
                                                height: 38,
                                                minWidth: 76,
                                                px: 1.15,
                                                borderRadius: "8px",
                                                background: financeLanguage === "tr" ? "#FFFFFF" : "transparent",
                                                color: financeLanguage === "tr" ? COLORS.navy : "#FFFFFF",
                                                boxShadow: financeLanguage === "tr" ? "0 3px 10px rgba(0,0,0,.18)" : "none",
                                                transition: "all .18s ease",
                                                "&:hover": {
                                                    background: financeLanguage === "tr" ? "#FFFFFF" : "rgba(255,255,255,.12)",
                                                },
                                            }}
                                        >
                                            <MDBox
                                                component="img"
                                                src="/flags/tr.svg"
                                                alt="Türkçe"
                                                sx={{ width: 24, height: 17, objectFit: "cover", borderRadius: "3px", display: "block" }}
                                            />
                                            <MDTypography component="span" sx={{ fontSize: "12px", fontWeight: 800, color: "inherit", lineHeight: 1 }}>
                                                TR
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                </Grid>
                            </Grid>
                        </MDBox>
                    </Card>
                </MDBox>

                <MDBox px={{ xs: 2, md: 3 }}>
                    <Grid container spacing={SPACE.rowGap} sx={{ mb: SPACE.sectionGap }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <FinanceKpiTile
                                title={financeText("Total Invoices")}
                                value={stats.total}
                                icon={<InboxIcon />}
                                tone="blue"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FinanceKpiTile
                                title={financeText("Linked")}
                                value={stats.linked}
                                icon={<CheckCircleIcon />}
                                tone="green"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FinanceKpiTile
                                title={financeText("Awaiting Link")}
                                value={stats.awaiting}
                                subtitle={
                                    stats.rejected
                                        ? `${stats.rejected} ${financeText("rejected invoices excluded")}`
                                        : undefined
                                }
                                icon={<HourglassTopIcon />}
                                tone="amber"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FinanceKpiTile
                                title={financeText("Total Amount")}
                                value={<CurrencyTotals lines={stats.amountLines} rates={exchangeRates} />}
                                subtitle={financeText("Excluding rejected invoices")}
                                icon={<AccountBalanceWalletIcon />}
                                tone="purple"
                            />
                        </Grid>
                    </Grid>

                    <StatusGuideCard />

                    <Card sx={{ ...cardSx, mb: SPACE.sectionGap }}>
                        <MDBox p={SPACE.cardPadding}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4} md={3}>
                                    <TextField
                                        type="date"
                                        fullWidth
                                        size="small"
                                        label={financeText("Start Date")}
                                        InputLabelProps={{ shrink: true }}
                                        value={range.startDate}
                                        onChange={(event) =>
                                            setRange((previous) => ({ ...previous, startDate: event.target.value }))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4} md={3}>
                                    <TextField
                                        type="date"
                                        fullWidth
                                        size="small"
                                        label={financeText("End Date")}
                                        InputLabelProps={{ shrink: true }}
                                        value={range.endDate}
                                        onChange={(event) =>
                                            setRange((previous) => ({ ...previous, endDate: event.target.value }))
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4} md={3}>
                                    <Tooltip title={financeText("EDM only allows a 2-month range per query.")}>
                                        <span style={{ display: "inline-flex" }}><MDButton
                                            variant="outlined"
                                            color="info"
                                            startIcon={<RefreshIcon />}
                                            onClick={fetchIncoming}
                                            disabled={loading}
                                        >
                                            {loading ? financeText("Loading...") : financeText("Refresh")}
                                        </MDButton></span>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </MDBox>
                    </Card>

                    {error && (
                        <Alert severity="error" sx={{ mb: SPACE.sectionGap }}>
                            {error}
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert severity="success" sx={{ mb: SPACE.sectionGap }}>
                            {successMessage}
                        </Alert>
                    )}

                    <FinanceCard>
                        <MDBox p={SPACE.cardPaddingLg}>
                            <FinanceSectionHeader
                                title={financeText("Incoming Invoices")}
                                subtitle={formatRecordCountLabel(invoices.length, financeLanguage)}
                            />

                            {loading ? (
                                <MDBox display="flex" justifyContent="center" py={6}>
                                    <CircularProgress size={30} />
                                </MDBox>
                            ) : invoices.length ? (
                                <DataTable
                                    table={tableData}
                                    isSorted={false}
                                    entriesPerPage={{ defaultValue: 25, entries: [10, 25, 50, 100] }}
                                    showTotalEntries
                                    noEndBorder
                                    onRowClick={openViewer}
                                />
                            ) : (
                                <MDBox display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
                                    <InboxIcon sx={{ fontSize: 40, color: COLORS.subtext }} />
                                    <MDTypography variant="button" sx={{ color: COLORS.subtext }}>
                                        {financeText("No incoming invoices found for this date range.")}
                                    </MDTypography>
                                </MDBox>
                            )}
                        </MDBox>
                    </FinanceCard>
                </MDBox>
            </MDBox>

            <Dialog
                open={Boolean(viewer)}
                onClose={closeViewer}
                fullWidth
                maxWidth="lg"
                PaperProps={{ sx: { borderRadius: "16px", height: "90vh" } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                        <MDBox minWidth={0}>
                            <MDTypography
                                sx={{ fontSize: 16, fontWeight: 700, color: COLORS.text, lineHeight: 1.3 }}
                                noWrap
                            >
                                {viewer?.invoice?.supplierName || financeText("View invoice")}
                            </MDTypography>
                            <MDTypography sx={{ fontSize: 12.5, color: COLORS.subtext }}>
                                {[
                                    viewer?.invoice?.id,
                                    formatDate(viewer?.invoice?.issueDate),
                                    viewer?.invoice && formatMoney(viewer.invoice.amount, viewer.invoice.currency),
                                ]
                                    .filter(Boolean)
                                    .join("  ·  ")}
                            </MDTypography>
                        </MDBox>
                        {viewer?.url && (
                            <MDBox display="flex" gap={1} flexShrink={0}>
                                <MDButton
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    component="a"
                                    href={viewer.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {financeText("Open in new tab")}
                                </MDButton>
                                <MDButton
                                    variant="gradient"
                                    color="info"
                                    size="small"
                                    component="a"
                                    href={viewer.url}
                                    download={`${viewer.invoice?.id || viewer.invoice?.uuid}.pdf`}
                                    startIcon={<CloudDownloadIcon sx={{ fontSize: "16px !important" }} />}
                                >
                                    {financeText("Download")}
                                </MDButton>
                            </MDBox>
                        )}
                    </MDBox>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: "flex", background: COLORS.muted }}>
                    {viewer?.loading ? (
                        <MDBox flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1.5}>
                            <CircularProgress size={30} />
                            <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                {financeText("Loading invoice…")}
                            </MDTypography>
                        </MDBox>
                    ) : viewer?.error ? (
                        <MDBox flex={1} p={3}>
                            <Alert severity="error">{viewer.error}</Alert>
                        </MDBox>
                    ) : viewer?.url ? (
                        <MDBox
                            component="iframe"
                            title={viewer.invoice?.id || "invoice"}
                            src={viewer.url}
                            sx={{ flex: 1, width: "100%", border: 0 }}
                        />
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <MDButton variant="text" color="secondary" onClick={closeViewer}>
                        {financeText("Close")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(linkTarget)}
                onClose={closeLinkDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: SPACE.radiusCard } }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LinkIcon sx={{ color: COLORS.blue, fontSize: 22 }} />
                    {financeText("Link to Job")}
                </DialogTitle>
                <DialogContent>
                    {linkTarget && (
                        <MDBox display="flex" flexDirection="column" gap={2} pt={0.5}>
                            <MDBox
                                p={1.5}
                                borderRadius={SPACE.radiusTile}
                                sx={{ background: COLORS.muted, border: `1px solid ${COLORS.border}` }}
                            >
                                <MDTypography variant="button" fontWeight="bold" display="block" sx={{ color: COLORS.text }}>
                                    {linkTarget.supplierName}
                                </MDTypography>
                                <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                    {linkTarget.id} · {formatDate(linkTarget.issueDate)} ·{" "}
                                    {formatMoney(linkTarget.amount, linkTarget.currency)}
                                </MDTypography>
                            </MDBox>

                            {/* Mode toggle — matching from open jobs is the
                                default/preferred path (visually picking the
                                right job beats blind text entry); manual
                                entry stays available as a fallback for jobs
                                that don't have a SupplierCost record yet. */}
                            <MDBox
                                display="flex"
                                gap={0.75}
                                p={0.5}
                                borderRadius={SPACE.radiusPill}
                                sx={{ background: COLORS.muted, border: `1px solid ${COLORS.border}` }}
                            >
                                <MDBox
                                    onClick={() => setLinkMode("match")}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={0.75}
                                    flex={1}
                                    py={0.85}
                                    borderRadius={SPACE.radiusPill}
                                    sx={{
                                        cursor: "pointer",
                                        transition: "all 0.15s ease",
                                        background: linkMode === "match" ? COLORS.blue : "transparent",
                                        color: linkMode === "match" ? COLORS.white : COLORS.subtext,
                                    }}
                                >
                                    <SearchIcon sx={{ fontSize: 17 }} />
                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: "inherit" }}>
                                        {financeText("Match Open Job")}
                                    </MDTypography>
                                </MDBox>
                                <MDBox
                                    onClick={() => setLinkMode("manual")}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={0.75}
                                    flex={1}
                                    py={0.85}
                                    borderRadius={SPACE.radiusPill}
                                    sx={{
                                        cursor: "pointer",
                                        transition: "all 0.15s ease",
                                        background: linkMode === "manual" ? COLORS.blue : "transparent",
                                        color: linkMode === "manual" ? COLORS.white : COLORS.subtext,
                                    }}
                                >
                                    <EditIcon sx={{ fontSize: 15 }} />
                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: "inherit" }}>
                                        {financeText("Manual Entry")}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            {linkMode === "match" ? (
                                selectedJob ? (
                                    <MDBox
                                        p={1.5}
                                        borderRadius={SPACE.radiusTile}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        gap={1}
                                        sx={{ background: COLORS.blueLight, border: `1px solid #B2DDFF` }}
                                    >
                                        <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                                            <CheckCircleIcon sx={{ color: COLORS.blue, fontSize: 20, flexShrink: 0 }} />
                                            <MDBox minWidth={0}>
                                                <MDTypography variant="button" fontWeight="bold" display="block" sx={{ color: COLORS.text }}>
                                                    {selectedJob.shipmentNo || financeText("Unknown Job")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{
                                                        color: COLORS.subtext,
                                                        display: "block",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {selectedJob.customerName || financeText("Unknown Customer")} ·{" "}
                                                    {financeText("Cost")} {formatMoney(selectedJob.expectedAmount, selectedJob.currency)} ·{" "}
                                                    {financeText("Selling")} {formatMoney(selectedJob.revenueAmount, selectedJob.revenueCurrency)}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                        <MDButton
                                            variant="text"
                                            color="info"
                                            size="small"
                                            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => setSelectedJob(null)}
                                            sx={{ flexShrink: 0 }}
                                        >
                                            {financeText("Change")}
                                        </MDButton>
                                    </MDBox>
                                ) : (
                                    <MDBox display="flex" flexDirection="column" gap={1}>
                                        <TextField
                                            autoFocus
                                            fullWidth
                                            size="small"
                                            placeholder={financeText("Search by job no, customer or supplier...")}
                                            value={jobSearchQuery}
                                            onChange={(event) => setJobSearchQuery(event.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ fontSize: 18, color: COLORS.subtext }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        <MDBox
                                            sx={{
                                                maxHeight: 260,
                                                overflowY: "auto",
                                                border: `1px solid ${COLORS.border}`,
                                                borderRadius: SPACE.radiusTile,
                                            }}
                                        >
                                            {loadingOpenJobs ? (
                                                <MDBox display="flex" justifyContent="center" py={4}>
                                                    <CircularProgress size={24} />
                                                </MDBox>
                                            ) : filteredOpenJobs.length ? (
                                                filteredOpenJobs.map((job, index) => (
                                                    <MDBox
                                                        key={job.financeJobId}
                                                        onClick={() => setSelectedJob(job)}
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        gap={1}
                                                        px={1.5}
                                                        py={1.1}
                                                        sx={{
                                                            cursor: "pointer",
                                                            borderTop: index === 0 ? "none" : `1px solid ${COLORS.border}`,
                                                            "&:hover": { background: COLORS.muted },
                                                        }}
                                                    >
                                                        <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                                                            <BusinessIcon sx={{ fontSize: 17, color: COLORS.subtext, flexShrink: 0 }} />
                                                            <MDBox minWidth={0}>
                                                                <MDTypography variant="button" fontWeight="bold" display="block" sx={{ color: COLORS.text }}>
                                                                    {job.shipmentNo || financeText("Unknown Job")}
                                                                </MDTypography>
                                                                <MDTypography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: COLORS.subtext,
                                                                        display: "block",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {job.customerName || financeText("Unknown Customer")}
                                                                    {job.supplierName ? ` · ${job.supplierName}` : ""}
                                                                </MDTypography>
                                                            </MDBox>
                                                        </MDBox>
                                                        <MDBox textAlign="right" flexShrink={0}>
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                display="block"
                                                                sx={{ color: COLORS.red, whiteSpace: "nowrap" }}
                                                            >
                                                                {financeText("Cost")} {formatMoney(job.expectedAmount, job.currency)}
                                                            </MDTypography>
                                                            <MDTypography
                                                                variant="caption"
                                                                display="block"
                                                                sx={{ color: COLORS.blue, whiteSpace: "nowrap" }}
                                                            >
                                                                {financeText("Selling")} {formatMoney(job.revenueAmount, job.revenueCurrency)}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                ))
                                            ) : (
                                                <MDBox display="flex" flexDirection="column" alignItems="center" gap={0.5} py={3} px={2}>
                                                    <InboxIcon sx={{ fontSize: 28, color: COLORS.subtext }} />
                                                    <MDTypography variant="caption" textAlign="center" sx={{ color: COLORS.subtext }}>
                                                        {financeText("No open jobs found")}.{" "}
                                                        {financeText("Try manual entry instead.")}
                                                    </MDTypography>
                                                </MDBox>
                                            )}
                                        </MDBox>
                                    </MDBox>
                                )
                            ) : (
                                <TextField
                                    autoFocus
                                    fullWidth
                                    size="small"
                                    label={financeText("Job / Shipment No")}
                                    placeholder="RHFL00248"
                                    value={linkShipmentNo}
                                    onChange={(event) => setLinkShipmentNo(event.target.value)}
                                />
                            )}

                            <TextField
                                type="date"
                                fullWidth
                                size="small"
                                label={financeText("Due Date")}
                                InputLabelProps={{ shrink: true }}
                                value={linkDueDate}
                                onChange={(event) => setLinkDueDate(event.target.value)}
                            />

                            {linkError && <Alert severity="error">{linkError}</Alert>}
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton variant="text" color="secondary" onClick={closeLinkDialog} disabled={linking}>
                        {financeText("Cancel")}
                    </MDButton>
                    <MDButton
                        variant="gradient"
                        color="info"
                        onClick={submitLink}
                        disabled={linking || (linkMode === "match" && !selectedJob)}
                    >
                        {linking ? financeText("Saving...") : financeText("Save")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(costWarning)}
                onClose={() => setCostWarning(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { border: `3px solid ${COLORS.red}`, borderRadius: SPACE.radiusCard } }}
            >
                <DialogContent sx={{ p: 0 }}>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        px={4}
                        py={3}
                        sx={{ background: COLORS.redLight, borderBottom: `1px solid ${COLORS.red}` }}
                    >
                        <ReportProblemIcon sx={{ color: COLORS.red, fontSize: 36 }} />
                        <MDTypography variant="h5" fontWeight="bold" sx={{ color: COLORS.red }}>
                            {financeText("Cost Mismatch Warning")}
                        </MDTypography>
                    </MDBox>

                    {costWarning && (
                        <MDBox px={4} py={3} display="flex" flexDirection="column" gap={2.5}>
                            <MDTypography variant="button" sx={{ color: COLORS.text, lineHeight: 1.6 }}>
                                {financeText("Job")} <b>{costWarning.shipmentNo}</b>
                                {": "}
                                {financeText("invoiced amount differs from the expected cost by")}{" "}
                                <b style={{ color: COLORS.red }}>
                                    {costWarning.differencePercent.toFixed(1)}%
                                </b>
                                {" — "}
                                {financeText("exceeds the")} {costWarning.toleranceLimitPercent}%{" "}
                                {financeText("tolerance limit.")}
                            </MDTypography>

                            <MDBox display="flex" gap={2}>
                                <MDBox
                                    flex={1}
                                    p={2}
                                    borderRadius={SPACE.radiusTile}
                                    sx={{ background: COLORS.muted, border: `1px solid ${COLORS.border}` }}
                                >
                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: COLORS.subtext }}>
                                        {financeText("Expected")}
                                    </MDTypography>
                                    <MDTypography variant="h6" fontWeight="bold" sx={{ color: COLORS.text }}>
                                        {formatMoney(costWarning.expectedAmount, costWarning.currency)}
                                    </MDTypography>
                                </MDBox>
                                <MDBox
                                    flex={1}
                                    p={2}
                                    borderRadius={SPACE.radiusTile}
                                    sx={{ background: COLORS.redLight, border: `1px solid #FECDCA` }}
                                >
                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: COLORS.red }}>
                                        {financeText("Invoiced")}
                                    </MDTypography>
                                    <MDTypography variant="h6" fontWeight="bold" sx={{ color: COLORS.red }}>
                                        {formatMoney(costWarning.invoicedAmount, costWarning.currency)}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            <MDTypography variant="caption" sx={{ color: COLORS.subtext, lineHeight: 1.5 }}>
                                {financeText("Amounts are VAT-exclusive, in the job's cost currency. Until approved, the job keeps its TMS cost; once approved, the invoiced total replaces it.")}
                            </MDTypography>

                            {mismatchError && <Alert severity="error">{mismatchError}</Alert>}
                        </MDBox>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 4, pb: 3, gap: 1 }}>
                    <MDButton
                        variant="outlined"
                        color="error"
                        onClick={() => setCostWarning(null)}
                        disabled={resolvingMismatch}
                    >
                        {financeText("Just Note It")}
                    </MDButton>
                    <MDButton
                        variant="gradient"
                        color="error"
                        onClick={resolveCostMismatch}
                        disabled={resolvingMismatch}
                    >
                        {resolvingMismatch
                            ? financeText("Updating...")
                            : financeText("Approve and Update Cost")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default IncomingInvoices;
